import {inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {cognitoOptions} from '../../../cognito-options';
import {GenericOAuth2} from '@capacitor-community/generic-oauth2';
import {assert, is} from 'typia';
import {StorageService} from '../storage.service';
import {environment} from '../../../../environments/environment';
import {FederatedOAuthState, Tokens} from "./auth.service";
import {isNative} from "../../utils";

export type FederatedAuthPlatform = 'google' | 'apple';

export interface FederatedAuthTokens {
  source: FederatedAuthPlatform;
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number;
}


interface OldRefreshResponse {
  access_token_response: {
    id_token: string;
    access_token: string;
    refresh_token: string;
    expires_in?: number;
    expires_at?: number
  }
}

interface NewRefreshResponse {
  access_token: string;
  id_token: string;
  refresh_token?: string;
  expires_in?: number;
  expires_at?: number
}

const identityProviderMap: Record<FederatedAuthPlatform, string> = {
  google: 'Google',
  apple: 'SignInWithApple',
}

// Cognito has definitively rejected our refresh token — the user must log in
// again. Any other failure (network, 5xx, plugin glitch, unknown shape) is
// transient and worth retrying without nuking the session.
class FatalRefreshError extends Error {
  override readonly name = 'FatalRefreshError';
}

const RETRY_DELAYS_MS = [500, 1500];

async function retryUnlessFatal<T>(fn: () => Promise<T>): Promise<T> {
  const attempts = RETRY_DELAYS_MS.length + 1;
  for (let i = 0; i < attempts; i++) {
    try {
      return await fn();
    } catch (err) {
      if (err instanceof FatalRefreshError || i === attempts - 1) throw err;
      await new Promise(resolve => setTimeout(resolve, RETRY_DELAYS_MS[i]));
    }
  }
  throw new Error('unreachable');
}

interface OAuthErrorLike {
  error: string;
}

function isInvalidGrantThrow(err: unknown): boolean {
  return is<OAuthErrorLike>(err) && err.error === 'invalid_grant';
}

async function isInvalidGrantResponse(resp: Response): Promise<boolean> {
  // RFC 6749 §5.2: fatal OAuth errors are 4xx with JSON body { error: "..." }.
  if (resp.status >= 500) return false;
  try {
    const body = await resp.clone().json();
    return is<OAuthErrorLike>(body) && body.error === 'invalid_grant';
  } catch {
    return false;
  }
}

@Injectable({providedIn: 'root'})
export class FederatedAuthService {
  private readonly storageService = inject(StorageService);
  private readonly router = inject(Router);
  private refreshPromise: Promise<Tokens | undefined> | undefined;

  /**
   * Initiates federated login.
   * On native platforms uses Capacitor plugin; on web does standard redirect.
   */
  public async federatedLogin(
    platform: FederatedAuthPlatform,
    oauthState: FederatedOAuthState,
  ): Promise<'success' | 'cancelled'> {
    const state = crypto.randomUUID();

    if (isNative) {
      // Native: use Capacitor OAuth2 plugin
      try {
        const result = await GenericOAuth2.authenticate({
          ...cognitoOptions,
          additionalParameters: {
            state,
            identity_provider: identityProviderMap[platform],
          }
        });

        const parsed = assert<{
          access_token_response: {
            id_token: string;
            access_token: string;
            refresh_token: string;
            expires_in?: number;
            expires_at?: number
          }
        }>(result);

        const resp = parsed.access_token_response;
        const expiresAt = resp.expires_at
          ? resp.expires_at
          : Date.now() + (resp.expires_in! * 1000);

        const tokens: FederatedAuthTokens = {
          source: platform,
          idToken: resp.id_token,
          accessToken: resp.access_token,
          refreshToken: resp.refresh_token,
          expiresAt
        };

        await this.storageService.setItem('AUTH_TOKENS', tokens);
        return 'success';
      } catch (err: any) {
        // if the user pressed “back” / closed the window, plugin rejects with USER_CANCELLED
        if (
          err === 'USER_CANCELLED' ||
          err.error === 'USER_CANCELLED' ||
          err.errorMessage === 'USER_CANCELLED' ||
          err.message === 'USER_CANCELLED'
        ) {
          // 🔇 hide it entirely
          return 'cancelled';
        }
        // otherwise handle or display the real error

        console.error('Error during native federated login', err);
        throw err;
      }
    } else {
      await this.storageService.setItem(
        'OATH_STATE',
        oauthState
      )
      const params = new URLSearchParams({
        response_type: 'code',
        client_id: cognitoOptions.appId,
        redirect_uri: cognitoOptions.web!.redirectUrl!,
        state,
        identity_provider: identityProviderMap[platform],
      });
      const url = `https://${environment.cognitoOAuthDomain}/oauth2/authorize?${params}`;
      if (typeof window === 'undefined') {
        throw new Error('Federated login requires a browser environment');
      }
      window.location.href = url;
      return new Promise<'success'>(() => {
      });
    }
  }

  /**
   * Handles the OAuth2 callback on web: exchanges code for tokens.
   * Call this from your OAuthCallbackComponent.
   */
  public async handleWebCallback(
    code: string
  ): Promise<FederatedOAuthState | undefined> {
    // Exchange code for tokens
    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      client_id: cognitoOptions.appId,
      redirect_uri: cognitoOptions.web!.redirectUrl!,
      code
    });

    const resp = await fetch(
      `https://${environment.cognitoOAuthDomain}/oauth2/token/`,
      {
        method: 'POST',
        headers: {'Content-Type': 'application/x-www-form-urlencoded'},
        body: body.toString()
      }
    );
    if (!resp.ok) {
      throw new Error(`Token exchange failed: ${resp.status}`);
    }

    const data = await resp.json();
    const expiresAt = Date.now() + data.expires_in * 1000;

    // The platform was written to AUTH_TYPE by BrowserAuthService.socialLogin
    // before the redirect to Cognito; recover it here so Apple users on web
    // don't end up with source: 'google' in their stored tokens.
    const authType = await this.storageService.getItem('AUTH_TYPE');
    const source: FederatedAuthPlatform = authType === 'apple' ? 'apple' : 'google';

    const tokens: FederatedAuthTokens = {
      source,
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token,
      expiresAt
    };
    await this.storageService.setItem('AUTH_TOKENS', tokens);
    const state = await this.storageService.getItem('OATH_STATE');
    await this.storageService.removeItem('OATH_STATE');
    return state;
  }

  public async getTokens(): Promise<Tokens | undefined> {
    const tokens = await this.storageService.getItem('AUTH_TOKENS');
    if (!tokens) return undefined;

    if (tokens.expiresAt < Date.now()) {
      return this.refreshDeduped(tokens);
    }

    return {
      source: tokens.source,
      idToken: tokens.idToken,
      accessToken: tokens.accessToken,
    };
  }

  public async forceRefresh(): Promise<Tokens | undefined> {
    const tokens = await this.storageService.getItem('AUTH_TOKENS');
    if (!tokens) return undefined;
    return this.refreshDeduped(tokens);
  }

  private refreshDeduped(tokens: FederatedAuthTokens): Promise<Tokens | undefined> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshWithRetry(tokens)
        .finally(() => this.refreshPromise = undefined);
    }
    return this.refreshPromise;
  }

  private async refreshWithRetry(tokens: FederatedAuthTokens): Promise<Tokens | undefined> {
    const expiredAt = new Date(tokens.expiresAt).toISOString();
    try {
      return await retryUnlessFatal(() => this.performRefresh(tokens));
    } catch (err) {
      if (err instanceof FatalRefreshError) {
        console.warn(`Refresh token rejected by Cognito (expired at ${expiredAt}), clearing tokens`, err);
        await this.storageService.removeItem('AUTH_TOKENS');
        return undefined;
      }
      // Transient. Tokens are kept so the next attempt can try again, but the
      // original error message is developer-facing ("Refresh failed: 503",
      // "unknown response format", …) — wrap it so GlobalErrorHandle's toast
      // shows something a user can actually act on. Original stays in console.
      console.warn(`Transient error refreshing token (expired at ${expiredAt}), keeping tokens for next attempt`, err);
      throw new Error('Connection problem. Please try again.');
    }
  }

  private async performRefresh(tokens: FederatedAuthTokens): Promise<Tokens> {
    return isNative ? this.refreshNativeTokens(tokens) : this.refreshWebTokens(tokens);
  }

  private async refreshNativeTokens(tokens: FederatedAuthTokens): Promise<Tokens> {
    let response: unknown;
    try {
      response = await GenericOAuth2.refreshToken({
        ...cognitoOptions,
        refreshToken: tokens.refreshToken,
      });
    } catch (err) {
      if (isInvalidGrantThrow(err)) {
        throw new FatalRefreshError(`invalid_grant from plugin: ${JSON.stringify(err)}`);
      }
      throw err;
    }

    if (!is<OldRefreshResponse | NewRefreshResponse>(response)) {
      // Keys only: the response carries the refresh and id tokens.
      console.error("Unknown token refresh response format", JSON.stringify(Object.keys(response ?? {})));
      throw new Error("Token refresh failed: unknown response format");
    }

    let newTokens: FederatedAuthTokens;
    if (is<OldRefreshResponse>(response)) {
      const resp = response.access_token_response;
      const expiresAt = resp.expires_at
        ? resp.expires_at
        : Date.now() + (resp.expires_in! * 1000);

      newTokens = {
        source: tokens.source,
        idToken: resp.id_token,
        accessToken: resp.access_token,
        refreshToken: resp.refresh_token,
        expiresAt
      };
    } else {
      const expiresAt = response.expires_at
        ? response.expires_at
        : Date.now() + (response.expires_in! * 1000);

      newTokens = {
        source: tokens.source,
        idToken: response.id_token,
        accessToken: response.access_token,
        refreshToken: response.refresh_token ?? tokens.refreshToken,
        expiresAt
      };
    }

    await this.storageService.setItem('AUTH_TOKENS', newTokens);
    return newTokens;
  }

  private async refreshWebTokens(tokens: FederatedAuthTokens): Promise<Tokens> {
    const body = new URLSearchParams({
      grant_type: 'refresh_token',
      client_id: cognitoOptions.appId,
      refresh_token: tokens.refreshToken,
    });

    const resp = await fetch(`https://${environment.cognitoOAuthDomain}/oauth2/token`, {
      method: 'POST',
      headers: {'Content-Type': 'application/x-www-form-urlencoded'},
      body: body.toString()
    });

    if (!resp.ok) {
      if (await isInvalidGrantResponse(resp)) {
        throw new FatalRefreshError(`invalid_grant from Cognito (status ${resp.status})`);
      }
      throw new Error(`Refresh failed: ${resp.status}`);
    }

    const data = await resp.json();
    const expiresAt = Date.now() + data.expires_in * 1000;
    const refreshed: FederatedAuthTokens = {
      source: tokens.source,
      idToken: data.id_token,
      accessToken: data.access_token,
      refreshToken: data.refresh_token || tokens.refreshToken,
      expiresAt
    };
    await this.storageService.setItem('AUTH_TOKENS', refreshed);
    return refreshed;
  }

  /**
   * Logs out user: clears storage and redirects.
   */
  public async logout(): Promise<void> {
    await this.storageService.removeItem('AUTH_TOKENS');
    const logoutUrl =
      `https://${environment.cognitoOAuthDomain}/logout?` +
      `client_id=${cognitoOptions.appId}&` +
      `response_type=code&` +
      `logout_uri=${encodeURIComponent(environment.baseUrl)}&` +
      `redirect_uri=${encodeURIComponent(cognitoOptions.web.redirectUrl)}`;

    if (isNative) {
      await GenericOAuth2.logout({logoutUrl});
    } else {
      if (typeof window === 'undefined') {
        throw new Error('Federated logout requires a browser environment');
      }
      window.location.href = logoutUrl;
      return new Promise<void>(() => {
      });
    }
  }
}
